import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { saveCurrent, PILLAR_LABELS } from "@/lib/instant";
import { Volume2, Headphones, CirclePlay, Image as ImageIcon, LayoutGrid } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];
const ORDER = ["respirer", "nourrir", "bouger", "toucher", "ressentir"];

const VIDEO_EXTS = ["mp4", "webm", "mov"];
const AUDIO_EXTS = ["mp3", "m4a", "wav", "ogg"];
const IMAGE_EXTS = ["png", "jpg", "jpeg", "webp"];
const extOf = (p) => (p || "").split(".").pop().toLowerCase();
// Format dominant d'un contenu : vidéo > audio > image (une image AVEC voix compte comme un audio)
const formatOf = (c) => {
  if (VIDEO_EXTS.includes(extOf(c.media_path)) || c.kind === "video") return "video";
  if (c.sound_path || AUDIO_EXTS.includes(extOf(c.media_path)) || c.kind === "audio") return "audio";
  if (IMAGE_EXTS.includes(extOf(c.media_path)) || c.kind === "image") return "image";
  return null; // contenus texte : pas de format média
};
// Les trois formats en gros blocs dès l'entrée (demande Julie, 20/10/2026)
const FORMATS = [
  { id: "audio", label: "Audios", Icon: Headphones },
  { id: "video", label: "Vidéos", Icon: CirclePlay },
  { id: "image", label: "Images", Icon: ImageIcon },
];

export default function Explorer() {
  const navigate = useNavigate();
  const [contents, setContents] = useState([]);
  const [pillar, setPillar] = useState(null); // null = tous les piliers
  const [format, setFormat] = useState(null); // null = rien de choisi
  const [locked, setLocked] = useState(false); // semaine de découverte terminée → à la carte verrouillé (402)

  useEffect(() => {
    // En-tête d'identité indispensable : sans elle, le verrouillage de fin de
    // découverte (402) ne peut pas s'appliquer — la visiteuse serait traitée en anonyme.
    const token = localStorage.getItem("rav-token");
    axios
      .get(`${API}/contents`, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then(({ data }) => setContents(data.contents))
      .catch((err) => {
        if (err?.response?.status === 402) setLocked(true);
      });
  }, []);

  const start = (c) => {
    saveCurrent({
      pillar: c.pillar,
      minutes: c.minutes,
      steps: [{ pillar: c.pillar, label: PILLAR_LABELS[c.pillar], minutes: c.minutes, title: c.title, description: c.description, kind: c.kind, sound: c.sound_path, media_path: c.media_path }],
    });
    navigate("/session");
  };

  const renderCard = (c, testId, delay) => (
    <motion.div
      key={c.id}
      className="rav-mini-card"
      data-testid={testId}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      <span className="rav-mini-card-title">
        « {c.title} »
        {c.sound_path && (
          <Volume2 size={14} strokeWidth={1.8} className="rav-sound-dot" data-testid={`${testId}-sound`} aria-label="Message audio" />
        )}
      </span>
      <span className="rav-mini-card-desc">{c.description}</span>
      <span className="rav-mini-card-foot">
        <span className="rav-mini-card-min">{c.minutes} min</span>
        <button type="button" className="rav-text-link" data-testid={`${testId}-start`} onClick={() => start(c)}>
          Commencer
        </button>
      </span>
    </motion.div>
  );

  const formatList = format ? contents.filter((c) => formatOf(c) === format) : [];

  return (
    <>
      <main className="rav-page" data-testid="explorer-page">
        <motion.img
          src="/logo-accueil.webp"
          alt="Respire Aime Vibre"
          className="rav-page-logo"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: EASE }}>
          <h1 className="rav-page-title rav-carte-title" data-testid="explorer-title">
            <LayoutGrid size={24} strokeWidth={1.6} aria-hidden="true" /> À la carte
          </h1>
          <p className="rav-page-sub" data-testid="explorer-subtitle">Choisissez librement votre pratique.</p>

          {locked ? (
            <div className="rav-limit-card" data-testid="explorer-locked" style={{ textAlign: "center", marginTop: 18 }}>
              <p style={{ fontWeight: 600, margin: 0 }}>Votre semaine de découverte est terminée 🤎</p>
              <p className="rav-page-sub" style={{ margin: "8px 0 14px" }}>
                Les contenus à la carte font partie de Respire Aime Vibre + — la boutique, elle, reste ouverte librement.
                Choisissez votre formule, sans engagement, pour tout retrouver.
              </p>
              <a className="rav-primary-button" data-testid="explorer-locked-cta" href="/offres" style={{ display: "inline-block", textDecoration: "none" }}>
                Découvrir les formules
              </a>
            </div>
          ) : (
          <>
          {/* Les trois formats en évidence dès l'entrée dans la rubrique */}
          <div className="rav-format-tiles" data-testid="explorer-format-tiles">
            {FORMATS.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                className={`rav-format-tile${format === id ? " is-active" : ""}`}
                data-testid={`explorer-format-${id}`}
                aria-pressed={format === id}
                onClick={() => setFormat(format === id ? null : id)}
              >
                <Icon size={30} strokeWidth={1.4} aria-hidden="true" />
                <span>{label}</span>
              </button>
            ))}
          </div>

          {format !== null && (
            <section className="rav-explorer-section" data-testid="explorer-format-list">
              <div className="rav-explorer-cards">
                {formatList.map((c, i) => renderCard(c, `content-format-${i}`, 0.1 + i * 0.05))}
                {formatList.length === 0 && (
                  <p className="rav-page-sub rav-explorer-hint" data-testid="explorer-format-empty">
                    Aucun contenu de ce format pour le moment — il arrive bientôt.
                  </p>
                )}
              </div>
            </section>
          )}

          <h2 className="rav-explorer-title rav-carte-pillars-title" data-testid="explorer-pillars-title">Choisir par pilier</h2>

          {/* Un tap sur un pilier → uniquement ses contenus ; retap → tout */}
          <div className="rav-explorer-chips" data-testid="explorer-pillar-chips">
            {ORDER.map((p) => (
              <button
                key={p}
                type="button"
                className={`rav-explorer-chip${pillar === p ? " is-active" : ""}`}
                data-testid={`explorer-pill-${p}`}
                aria-pressed={pillar === p}
                onClick={() => setPillar(pillar === p ? null : p)}
              >
                <img src={`/needs/${p}.webp`} alt="" aria-hidden="true" className="rav-explorer-chip-icon" loading="lazy" decoding="async" />
                {PILLAR_LABELS[p]}
              </button>
            ))}
          </div>
          {pillar === null ? (
            ORDER.map((p, pi) => (
              <section key={p} className="rav-explorer-section" data-testid={`explorer-${p}`}>
                <div className="rav-explorer-head">
                  <img src={`/needs/${p}.webp`} alt="" aria-hidden="true" className="rav-explorer-icon" loading="lazy" decoding="async" />
                  <h2 className="rav-explorer-title">{PILLAR_LABELS[p]}</h2>
                </div>
                <div className="rav-explorer-cards">
                  {contents.filter((c) => c.pillar === p).map((c, i) => renderCard(c, `content-${p}-${i}`, 0.15 + (pi * 3 + i) * 0.05))}
                </div>
              </section>
            ))
          ) : (
            <section className="rav-explorer-section" data-testid="explorer-pillar-list">
              <div className="rav-explorer-head">
                <img src={`/needs/${pillar}.webp`} alt="" aria-hidden="true" className="rav-explorer-icon" loading="lazy" decoding="async" />
                <h2 className="rav-explorer-title">{PILLAR_LABELS[pillar]}</h2>
              </div>
              <div className="rav-explorer-cards">
                {contents.filter((c) => c.pillar === pillar).map((c, i) => renderCard(c, `content-pill-${i}`, 0.1 + i * 0.05))}
              </div>
            </section>
          )}

          <section className="rav-explorer-section" data-testid="explorer-enfants">
            <div className="rav-explorer-head">
              <img src="/needs/ressentir.webp" alt="" aria-hidden="true" className="rav-explorer-icon" />
              <h2 className="rav-explorer-title">Pour les enfants</h2>
            </div>
            <p className="rav-page-sub rav-explorer-sub">Des petits moments à partager avec eux, en douceur.</p>
            <div className="rav-explorer-cards">
              {contents
                .filter((c) => c.audience === "enfant")
                .map((c, i) => renderCard(c, `content-enfant-${i}`, 0.1 + i * 0.06))}
            </div>
          </section>
          </>
          )}
        </motion.div>
      </main>
    </>
  );
}
